import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, Spinner } from "@/components/Common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { openPortal } from "@/lib/billing";

export const DangerZone = () => {
  const { billing, tier } = useAuth();
  const [open, setOpen] = useState(false);
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const paid = tier !== "free" && !billing?.trial_active;
  const del = async () => {
    setBusy(true);
    try {
      await api.delete("/me");
      Object.keys(localStorage).filter((k) => k.startsWith("rpopro")).forEach((k) => localStorage.removeItem(k));
      toast.success("Your account and data have been deleted");
      window.dispatchEvent(new CustomEvent("rpopro:logout"));
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <Card className="border-red-500/30" id="danger-zone" data-testid="danger-zone-card">
      <div className="mb-2 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-400" /><h3 className="font-display text-lg text-zinc-100">Danger zone</h3></div>
      <p className="text-sm text-zinc-400">Permanently delete your RPOPRO account and all data — profile, resumes, saved jobs, tracker, chats and scores — on web and mobile.</p>
      <Button variant="outline" onClick={() => { setTxt(""); setOpen(true); }} className="mt-4 w-full border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300" data-testid="delete-account-button"><Trash2 className="h-4 w-4" />Delete my account & data</Button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="border-red-500/40 bg-[#141417] sm:max-w-md" data-testid="delete-account-dialog">
          <DialogTitle className="font-display text-zinc-100">Delete account & data?</DialogTitle>
          <DialogDescription className="text-zinc-400">This can't be undone. Everything linked to your account is erased on web and mobile.</DialogDescription>
          {paid && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200" data-testid="delete-subscription-warning">
              You have an active {tier} subscription. Deleting your account doesn't cancel billing.{" "}
              <button onClick={() => openPortal("/profile")} className="font-medium underline" data-testid="delete-cancel-subscription-link">Cancel subscription first</button>
            </div>
          )}
          <label className="text-sm text-zinc-300">Type <span className="font-mono text-red-400">DELETE</span> to confirm</label>
          <Input value={txt} onChange={(e) => setTxt(e.target.value)} className="bg-[#09090B]" autoComplete="off" data-testid="delete-confirm-input" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy} data-testid="delete-cancel-button">Cancel</Button>
            <Button onClick={del} disabled={txt !== "DELETE" || busy} className="bg-red-600 text-white hover:bg-red-500" data-testid="delete-confirm-button">{busy ? <Spinner /> : "Delete forever"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
